import { useEffect, useRef, useState } from "react";

type Props = {
  children: string;
};

// Characters per second to reveal (good for short tool status text).
const speed = 150;

export function TypewriterSpan({ children: text }: Props) {
  // On initial mount, start with all existing text revealed (no animation on page refresh)
  const [revealedLength, setRevealedLength] = useState(() => text.length);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const revealedRef = useRef(text.length);
  const textRef = useRef(text);
  const speedRef = useRef(speed);
  // Track if we've ever been "caught up" - if we mount with text, we're caught up
  const hasBeenCaughtUpRef = useRef(text.length > 0);

  // Keep refs in sync
  revealedRef.current = revealedLength;
  textRef.current = text;
  speedRef.current = speed;

  useEffect(() => {
    if (animationRef.current !== null) return;

    // If already fully revealed, mark caught up
    if (revealedRef.current >= text.length) {
      if (text.length > 0) {
        hasBeenCaughtUpRef.current = true;
      }
      return;
    }

    // If large jump and not caught up yet AND we already have some revealed text, snap (page refresh)
    // If starting from 0 (empty), always animate - this is the normal case for tools waiting for data
    const textJump = text.length - revealedRef.current;
    if (
      textJump >= 15 &&
      !hasBeenCaughtUpRef.current &&
      revealedRef.current > 0
    ) {
      setRevealedLength(text.length);
      revealedRef.current = text.length;
      hasBeenCaughtUpRef.current = true;
      return;
    }

    hasBeenCaughtUpRef.current = true;

    const animate = (time: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = time;
      }

      const msPerChar = 1000 / speedRef.current;
      const elapsed = time - lastTimeRef.current;
      const charsToAdd = Math.floor(elapsed / msPerChar);

      if (charsToAdd > 0) {
        const newRevealed = Math.min(
          revealedRef.current + charsToAdd,
          textRef.current.length
        );
        revealedRef.current = newRevealed;
        setRevealedLength(newRevealed);
        lastTimeRef.current = time;
      }

      if (revealedRef.current < textRef.current.length) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        lastTimeRef.current = 0;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [text]);

  useEffect(() => {
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return <>{text.slice(0, revealedLength)}</>;
}
